import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSave, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import API_URL from '../config';

const ProcessingView = ({ recordingId, progress, message, patientInfo, setPatientInfo, status }) => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    height: '',
    weight: '',
    patient_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (patientInfo && !saving) {
      setFormData({
        name: patientInfo.name || '',
        age: patientInfo.age || '',
        gender: patientInfo.gender || '',
        height: patientInfo.height || '',
        weight: patientInfo.weight || '',
        patient_id: patientInfo.patient_id || ''
      });
    }
  }, [patientInfo]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setSaveSuccess(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/patient/${patientInfo.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSaveSuccess(true);
        const updatedData = await response.json();
        setPatientInfo(prev => ({ ...prev, ...updatedData }));
      } else {
        alert("Failed to save patient metadata");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving metadata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 mt-8">
      {/* Progress Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-8 text-center">
        <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-5xl mb-6" />
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {status === 'queued' ? 'Queued for Processing...' : 'Processing Recording...'}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-lg mx-auto">
          {status === 'queued'
            ? 'Your recording has been accepted and is waiting for the workflow runner to start. This usually only takes a short moment.'
            : 'Our specialized ML pipelines and rule-based stages are actively processing your EDF recording. This may take several minutes depending on the recording duration.'}
        </p>

        {/* Progress Bar Container */}
        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-4 mb-2 overflow-hidden shadow-inner">
          <div 
            className="bg-blue-600 h-4 rounded-full transition-all duration-1000 ease-in-out relative" 
            style={{ width: `${Math.max(5, progress)}%` }}
          >
            <div className="absolute top-0 left-0 right-0 bottom-0 bg-white/20 animate-pulse"></div>
          </div>
        </div>
        
        <div className="flex justify-between items-center text-sm font-medium">
          <span className="text-blue-600 dark:text-blue-400">
            {message && message !== "Processing Pipeline"
              ? message
              : status === 'queued'
                ? 'Queued for workflow'
                : 'Processing Pipeline'}
          </span>
          <span className="text-gray-700 dark:text-gray-300">{Math.round(progress)}% Complete</span>
        </div>
      </div>

      {/* Metadata Editor */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100">Review Patient Metadata</h3>
          {saveSuccess && (
            <span className="text-green-600 text-sm font-medium flex items-center">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Saved!
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Some EDF headers do not contain complete patient information. 
          While processing occurs, you can proactively fill out or correct the patient metadata to ensure 
          the final generated report is accurate.
        </p>

        <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject Name</label>
            <input 
              type="text" 
              name="name" 
              value={formData.name} 
              onChange={handleChange} 
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none" 
            />
          </div>
          <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject ID</label>
             <input 
                type="text" 
                name="patient_id" 
                value={formData.patient_id} 
                onChange={handleChange} 
                className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none" 
             />
          </div>
          <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Age (Years)</label>
             <input 
                type="number" 
                name="age" 
                value={formData.age} 
                onChange={handleChange} 
                className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none" 
             />
          </div>
          <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gender</label>
             <select 
               name="gender" 
               value={formData.gender} 
               onChange={handleChange}
               className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
             >
               <option value="">Select...</option>
               <option value="Male">Male</option>
               <option value="Female">Female</option>
               <option value="Other">Other</option>
             </select>
          </div>
          <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Height (e.g. 185 cm)</label>
             <input 
                type="text" 
                name="height" 
                value={formData.height} 
                onChange={handleChange} 
                className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none" 
             />
          </div>
          <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Weight (e.g. 85 kg)</label>
             <input 
                type="text" 
                name="weight" 
                value={formData.weight} 
                onChange={handleChange} 
                className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none" 
             />
          </div>
          <div className="md:col-span-2 pt-2 border-t border-gray-100 dark:border-gray-700">
             <button 
                type="submit" 
                disabled={saving}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-indigo-300 text-white font-medium rounded-lg transition-colors shadow-sm mt-2 flex justify-center items-center"
             >
               {saving ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={faSave} className="mr-2" />}
               {saving ? 'Saving...' : 'Save Metadata'}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProcessingView;
